import { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import WebhookIcon from '@mui/icons-material/Webhook';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listWebhooks, listTemplates } from '../api/discord';
import { usePermission } from '../hooks/usePermission';
import { WebhookCard } from '../components/discord/WebhookCard';
import { TemplateCard } from '../components/discord/TemplateCard';
import { AddWebhookModal } from '../components/discord/AddWebhookModal';
import { AddTemplateModal } from '../components/discord/AddTemplateModal';

export function DiscordWebhooks() {
  const canWrite = usePermission('discord.write');
  const qc = useQueryClient();
  const [addWebhookOpen, setAddWebhookOpen] = useState(false);
  const [addTemplateOpen, setAddTemplateOpen] = useState(false);

  const { data: webhooks = [] } = useQuery({
    queryKey: ['discord', 'webhooks'],
    queryFn: listWebhooks,
    refetchOnWindowFocus: true,
  });

  const { data: templates = [] } = useQuery({
    queryKey: ['discord', 'templates'],
    queryFn: listTemplates,
    refetchOnWindowFocus: true,
  });

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ['discord'] });
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          Discord Notifications
        </Typography>
      </Stack>

      {/* Webhooks section */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <WebhookIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={600}>
            Webhooks
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ({webhooks.length})
          </Typography>
        </Stack>
        {canWrite && (
          <Button
            startIcon={<AddIcon />}
            size="small"
            variant="outlined"
            onClick={() => setAddWebhookOpen(true)}
          >
            Add Webhook
          </Button>
        )}
      </Stack>

      {webhooks.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          No webhooks configured yet.
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          {webhooks.map((w) => (
            <WebhookCard key={w.id} webhook={w} canWrite={canWrite} onChanged={invalidate} />
          ))}
        </Stack>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Templates section */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <NotificationsIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={600}>
            Notification Templates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ({templates.length})
          </Typography>
        </Stack>
        {canWrite && webhooks.length > 0 && (
          <Button
            startIcon={<AddIcon />}
            size="small"
            variant="outlined"
            onClick={() => setAddTemplateOpen(true)}
          >
            Add Template
          </Button>
        )}
      </Stack>

      {templates.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {webhooks.length === 0
            ? 'Add a webhook first, then create notification templates.'
            : 'No templates yet. Add one to start receiving notifications.'}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {templates.map((t) => (
            <TemplateCard
              key={t.id}
              template={t}
              webhooks={webhooks}
              canWrite={canWrite}
              onChanged={invalidate}
            />
          ))}
        </Stack>
      )}

      <AddWebhookModal
        open={addWebhookOpen}
        onClose={() => setAddWebhookOpen(false)}
        onCreated={() => {
          setAddWebhookOpen(false);
          invalidate();
        }}
      />
      <AddTemplateModal
        open={addTemplateOpen}
        webhooks={webhooks}
        onClose={() => setAddTemplateOpen(false)}
        onCreated={() => {
          setAddTemplateOpen(false);
          invalidate();
        }}
      />
    </Box>
  );
}
